import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { ConfirmationResult } from "firebase/auth";
import { signInWithPhoneNumber } from "firebase/auth";
import { auth, getRecaptchaVerifier } from "../../../auth/firebase";
import { OtpVerification } from "./OtpVerification";
import { getAuthErrorMessage, hasPendingTryNowJourney, resolvePostFirebaseLogin } from "../../../hallticket-lib";

interface OtpLocationState {
  mobile: string;
  postAuthRedirect?: string;
}

function getConfirmationResult(): ConfirmationResult | null {
  return (window as unknown as { confirmationResult?: ConfirmationResult }).confirmationResult ?? null;
}

export function OtpRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as OtpLocationState | null;
  const mobile = state?.mobile;
  const postAuthRedirect = state?.postAuthRedirect === "/dashboard/results" ? state.postAuthRedirect : null;
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const initialOtpRequestedFor = useRef<string | null>(null);

  const sendOtp = async (targetMobile: string, isResend: boolean) => {
    if (isResend) {
      setIsResending(true);
    }
    setVerificationError(null);

    try {
      const appVerifier = getRecaptchaVerifier();
      const phoneNumber = `+91${targetMobile}`;
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);

      (window as any).confirmationResult = confirmationResult;
    } catch (err) {
      console.error(err);
      setVerificationError(isResend ? "Could not resend OTP. Please try again." : "Could not send OTP. Please try again.");
    } finally {
      if (isResend) {
        setIsResending(false);
      }
    }
  };

  useEffect(() => {
    if (!mobile) {
      return;
    }
    if (initialOtpRequestedFor.current === mobile) {
      return;
    }

    initialOtpRequestedFor.current = mobile;
    void sendOtp(mobile, false);
  }, [mobile]);

  // useEffect(() => {
  //   return () => {
  //     clearRecaptchaVerifier();
  //   };
  // }, []);

  const handleVerify = async (otp: string) => {
    const confirmationResult = getConfirmationResult();
    if (!confirmationResult) {
      navigate("/signin", { replace: true });
      return;
    }

    setVerificationError(null);
    setIsVerifying(true);
    try {
      const userCredential = await confirmationResult.confirm(otp);
      const idToken = await userCredential.user.getIdToken(true);
      const authState = await resolvePostFirebaseLogin(idToken);

      if (authState.status === "signed_in") {
        navigate(postAuthRedirect ?? (hasPendingTryNowJourney() ? "/dashboard/results" : "/dashboard"), { replace: true });
      } else {
        navigate("/terms", { replace: true, state: { mobile, postAuthRedirect } });
      }
    } catch (error) {
      const detail = getAuthErrorMessage(error);
      setVerificationError(detail);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBack = () => {
    navigate("/signin");
  };

  const handleResend = async () => {
    if (!mobile || isResending) {
      return;
    }
    await sendOtp(mobile, true);
  };

  if (!mobile) {
    return <Navigate to="/signin" replace />;
  }

  return (
    <OtpVerification
      mobile={mobile}
      onVerify={handleVerify}
      onBack={handleBack}
      verificationError={verificationError}
      onClearVerificationError={() => setVerificationError(null)}
      onResend={handleResend}
      isResending={isResending}
      isVerifying={isVerifying}
    />
  );
}
